import { useState, useCallback, useEffect, useRef } from "react";
import { transformData } from "../../../utils/data-transform";
import { ms2Regions } from "../config/layers";

export const ALTERNATIVE_ANALYSIS_CATEGORY = "Alternative Analysis Model";

export const AA_OUTLINE_COLOR = "#E8A87C"; // AA yellow fill
export const AA_LINE_COLOR = "#FF9100"; // vivid orange outline
export const AA_SELECTED_COLOR = "#FF9100"; // even brighter when selected
export const AA_SELECTED_LINE_COLOR = "#FF6D00"; // deep orange selected stroke
export const BASELINE_FILL_COLOR = "#0369A1";
export const BASELINE_LINE_COLOR = "#075985";
export const BASELINE_SELECTED_COLOR = "#0EA5E9";
export const BASELINE_SELECTED_LINE_COLOR = "#38BDF8";
export const RAS_OUTLINE_COLORS = [AA_OUTLINE_COLOR];

export const BASELINE_CATEGORY = "Baseline Model";

function categoryDrawOrder(feature) {
  const category = feature?.properties?.model_category;
  if (category === BASELINE_CATEGORY) return 0;
  if (category === ALTERNATIVE_ANALYSIS_CATEGORY) return 1;
  return 2;
}

const INTERACTIVE_BOUNDARY_LAYERS = [
  "dmqt-layer-boundaries-fill",
  "dmqt-layer-boundaries-lines",
  "dmqt-layer-boundaries-hit",
];

export function assignRasOutlineColors(features = []) {
  return features.map((feature) => {
    const isAlternative =
      feature?.properties?.model_category === ALTERNATIVE_ANALYSIS_CATEGORY;
    if (!isAlternative) return feature;
    return {
      ...feature,
      properties: {
        ...feature.properties,
        outline_color: AA_OUTLINE_COLOR,
      },
    };
  });
}

const AA_SOFTWARE_STOP_WORDS = new Set([
  "huc",
  "ras",
  "hms",
  "hec",
  "model",
  "alternative",
  "analysis",
  "baseline",
  "watershed",
]);

export function distinctiveNameTokens(text) {
  return String(text || "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(
      (token) =>
        token.length >= 5 &&
        !AA_SOFTWARE_STOP_WORDS.has(token) &&
        !token.startsWith("huc"),
    );
}

export function isRasOrHmsModel(model) {
  const software = (model?.model_software_name || "").toLowerCase();
  return software.includes("ras") || software.includes("hms");
}

export function uniqueClickedFeatures(
  eventFeatures = [],
  renderedFeatures = [],
) {
  const unique = [];
  const seen = new Set();
  [...eventFeatures, ...renderedFeatures].forEach((feature) => {
    if (!feature) return;
    const key = [
      feature.id,
      feature.properties?.model_category,
      feature.properties?.ref_value,
    ].join(":");
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(feature);
  });
  return unique;
}

export function featureByCategory(features = [], category) {
  return features.find(
    (feature) => feature?.properties?.model_category === category,
  );
}

export function isBaselineLayerActive(activeMap) {
  if (!activeMap?.keys) return false;
  for (const key of activeMap.keys()) {
    if (String(key).includes(BASELINE_CATEGORY)) return true;
  }
  return false;
}

export function baselineSearchFromFeature(properties = {}) {
  const huc = String(properties.huc_code || "")
    .split(",")[0]
    .trim();
  if (huc) {
    return {
      key: "huc_code",
      value: huc,
      modelCategory: BASELINE_CATEGORY,
    };
  }
  if (properties.ref_key && properties.ref_value) {
    return {
      key: properties.ref_key,
      value: properties.ref_value,
      modelCategory: BASELINE_CATEGORY,
    };
  }
  return null;
}

export function mergeLayerModels(...groups) {
  const merged = [];
  const seen = new Set();
  groups.flat().forEach((model) => {
    if (!model) return;
    const id = `${model.tdis_identifier || model.name}:${model.model_category || ""}`;
    if (seen.has(id)) return;
    seen.add(id);
    merged.push(model);
  });
  return merged;
}

export function aaSearchFromFeature(properties = {}) {
  const huc = String(properties.huc_code || "")
    .split(",")[0]
    .trim();
  if (huc) {
    return {
      key: "huc_code",
      value: huc,
      modelCategory: ALTERNATIVE_ANALYSIS_CATEGORY,
    };
  }
  return {
    key: properties.ref_key || "tdis_identifier",
    value: properties.ref_value,
    modelCategory: ALTERNATIVE_ANALYSIS_CATEGORY,
  };
}

export function modelsForClickedCoordinate(models = [], properties = {}) {
  const rasHms = models.filter(isRasOrHmsModel);
  const candidates = rasHms.length ? rasHms : models;
  const clickedIds = []
    .concat(properties.ref_value || [])
    .map((id) => String(id));
  const tokens = distinctiveNameTokens(
    [properties.model_name, properties.display_name, properties.file_name]
      .filter(Boolean)
      .join(" "),
  );

  const named = tokens.length
    ? candidates.filter((model) => {
        const modelTokens = distinctiveNameTokens(
          `${model.name || ""} ${model.model_name || ""}`,
        );
        return tokens.some((token) =>
          modelTokens.some(
            (item) => item.includes(token) || token.includes(item),
          ),
        );
      })
    : [];
  const clicked = candidates.filter((model) =>
    clickedIds.includes(String(model.tdis_identifier)),
  );
  const merged = [];
  const seen = new Set();
  [...named, ...clicked].forEach((model) => {
    const id = model.tdis_identifier || model.name;
    if (seen.has(id)) return;
    seen.add(id);
    merged.push(model);
  });
  if (merged.length) return merged;
  if (candidates.length <= 4) return candidates;
  return clicked.length ? clicked : candidates;
}

/**
 * Custom hook for managing layer data and operations
 */
const useLayerData = ({
  apiClient,
  apiType,
  headers = {},
  mapRef,
  isAuthenticated,
  setShowAuthDialog,
}) => {
  // Layer state
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [layerModels, setLayerModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [hasMoreModels, setHasMoreModels] = useState(false);
  const [modelPage, setModelPage] = useState(1);
  const [totalModels, setTotalModels] = useState(0);
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [searchData, setSearchData] = useState({});
  const authenticated = useRef(isAuthenticated);

  // Memoize headers to ensure stability
  const currentHeaders = useRef(headers);
  const activeFeatures = useRef(new Map());

  // Debug headers when they change
  useEffect(() => {
    if (headers && Object.keys(headers).length > 0) {
      // console.log('🔄 Updating internal currentHeaders:', headers);
      currentHeaders.current = headers;
      authenticated.current = isAuthenticated;
    }
  }, [headers, isAuthenticated]);

  // Fetch layer models
  const fetchLayerModels = useCallback(
    async (searchBy, searchValue, page = 1, modelCategory, options = {}) => {
      if (!searchBy) return;
      const applyState = options.applyState !== false;
      try {
        if (applyState) setLoadingModels(true);
        let value = searchValue;
        try {
          value = JSON.parse(searchValue);
          if (Array.isArray(value)) {
            searchValue = value.join(",");
          }
        } catch (parseError) {
          console.debug(
            "Keeping raw search value; JSON parse skipped",
            parseError,
          );
        }

        const query = { key: searchBy, value: searchValue };
        if (modelCategory) {
          query.model_category = modelCategory;
        }

        const requestData = {
          query,
          page: page,
          size: 50,
          count: totalModels || 0,
        };

        const response = await apiClient.postRequest(
          "/keyword/search",
          requestData,
          apiType,
          currentHeaders.current,
        );

        if (response.pageinfo.count === 0) {
          if (applyState) {
            setLayerModels([]);
            setHasMoreModels(false);
            setTotalModels(0);
          }
          return [];
        }

        const transformedData = transformData(response);
        if (applyState) {
          setTotalModels(response.pageinfo.count);

          const currentModelsCount = page === 1 ? 0 : layerModels.length;
          const newTotalLoaded = currentModelsCount + transformedData.length;
          const hasMore = newTotalLoaded < response.pageinfo.count;

          if (page === 1) {
            setLayerModels(transformedData);
          } else {
            setLayerModels((prev) => [...prev, ...transformedData]);
          }
          setHasMoreModels(hasMore);
        }
        return transformedData;
      } catch (error) {
        console.error("Error fetching layer models:", error);
        return [];
      } finally {
        if (applyState) setLoadingModels(false);
      }
    },
    [apiClient, apiType, layerModels, totalModels],
  );

  // Add layer event listeners
  const addLayerEventListeners = useCallback(
    (map) => {
      let hoveredCountyId = null;
      let selectedCountyIds = new Set();
      let lastClickAt = 0;

      INTERACTIVE_BOUNDARY_LAYERS.forEach((layerId) => {
        map.off("click", layerId);
        map.off("mousemove", layerId);
        map.off("mouseleave", layerId);
      });

      const isSelectedId = (id) => selectedCountyIds.has(id);

      const handleFeatureHover = (e) => {
        map.getCanvas().style.cursor = "pointer";

        if (e.features.length > 0) {
          if (hoveredCountyId !== null && !isSelectedId(hoveredCountyId)) {
            map.setFeatureState(
              { source: "dmqt-layer-boundaries", id: hoveredCountyId },
              { hover: false },
            );
          }

          hoveredCountyId = e.features[0].id;
          if (!isSelectedId(hoveredCountyId)) {
            map.setFeatureState(
              { source: "dmqt-layer-boundaries", id: hoveredCountyId },
              { hover: true },
            );
          }
        }
      };

      const handleFeatureLeave = () => {
        map.getCanvas().style.cursor = "";
        if (hoveredCountyId !== null && !isSelectedId(hoveredCountyId)) {
          map.setFeatureState(
            { source: "dmqt-layer-boundaries", id: hoveredCountyId },
            { hover: false },
          );
        }
        hoveredCountyId = null;
      };

      const selectFeatures = (features) => {
        selectedCountyIds.forEach((id) => {
          map.setFeatureState(
            { source: "dmqt-layer-boundaries", id },
            { selected: false },
          );
        });
        selectedCountyIds = new Set();
        features.forEach((feature) => {
          if (feature?.id == null) return;
          selectedCountyIds.add(feature.id);
          map.setFeatureState(
            { source: "dmqt-layer-boundaries", id: feature.id },
            { selected: true },
          );
        });
      };

      const handleFeatureClick = async (e) => {
        if (!e.features?.length) return;

        const now = Date.now();
        if (now - lastClickAt < 50) return;
        lastClickAt = now;

        if (!authenticated.current) {
          setShowAuthDialog(true);
          return;
        }

        const rendered =
          e.point && typeof map.queryRenderedFeatures === "function"
            ? map.queryRenderedFeatures(e.point, {
                layers: INTERACTIVE_BOUNDARY_LAYERS.filter((id) =>
                  map.getLayer(id),
                ),
              })
            : [];
        const clicked = uniqueClickedFeatures(e.features, rendered);
        if (!clicked.length) return;

        const aaFeature = featureByCategory(
          clicked,
          ALTERNATIVE_ANALYSIS_CATEGORY,
        );
        const blFeature = featureByCategory(clicked, BASELINE_CATEGORY);
        // Prefer the category under the cursor: AA click stays AA-only even when
        // Baseline is also toggled on.
        const feature = aaFeature || blFeature || clicked[0];
        selectFeatures([feature].filter(Boolean));

        const searchBy = feature.properties["ref_key"];
        const searchValue = feature.properties["ref_value"];
        let displayName = feature.properties["display_name"];
        const region = ms2Regions.find((item) => item.id === searchBy);
        if (region) {
          displayName = region.name;
        }

        const includeAa = Boolean(aaFeature);

        setSelectedLayer(displayName);
        setIsPanelVisible(true);
        setModelPage(1);
        setLayerModels([]);
        setHasMoreModels(false);
        setTotalModels(0);

        const aaSearch = includeAa
          ? aaSearchFromFeature(feature.properties)
          : null;
        const searchKey = aaSearch ? aaSearch.key : searchBy;
        const searchVal = aaSearch ? aaSearch.value : searchValue;
        const modelCategory = aaSearch
          ? aaSearch.modelCategory
          : feature.properties?.model_category;

        setSearchData({
          key: searchKey,
          value: searchVal,
          modelCategory,
        });

        const models = await fetchLayerModels(
          searchKey,
          searchVal,
          1,
          modelCategory,
        );
        if (includeAa && models?.length) {
          const scoped = modelsForClickedCoordinate(models, feature.properties);
          setLayerModels(scoped);
          setHasMoreModels(false);
          setTotalModels(scoped.length);
        }
      };

      INTERACTIVE_BOUNDARY_LAYERS.forEach((layerId) => {
        map.on("mousemove", layerId, handleFeatureHover);
        map.on("mouseleave", layerId, handleFeatureLeave);
        map.on("click", layerId, handleFeatureClick);
      });
    },
    [fetchLayerModels, setShowAuthDialog],
  );

  // Add layer layers
  const addLayerLayers = useCallback(
    (layer, geojsonData) => {
      const map = mapRef.current?.getMap();
      if (!map) return;

      // Update or add the county boundaries source
      if (map.getSource("dmqt-layer-boundaries")) {
        map.getSource("dmqt-layer-boundaries").setData(geojsonData);
      } else {
        map.addSource("dmqt-layer-boundaries", {
          type: "geojson",
          data: geojsonData,
          generateId: true,
        });
      }

      // Define layer configurations
      const layers = [
        {
          id: "dmqt-layer-boundaries-fill",
          type: "fill",
          source: "dmqt-layer-boundaries",
          filter: [
            "match",
            ["geometry-type"],
            ["Polygon", "MultiPolygon"],
            true,
            false,
          ],
          paint: {
            "fill-color": [
              "case",
              [
                "all",
                ["boolean", ["feature-state", "selected"], false],
                [
                  "==",
                  ["get", "model_category"],
                  ALTERNATIVE_ANALYSIS_CATEGORY,
                ],
              ],
              AA_SELECTED_COLOR,
              ["boolean", ["feature-state", "selected"], false],
              BASELINE_SELECTED_COLOR,
              ["has", "outline_color"],
              ["get", "outline_color"],
              ["==", ["get", "model_category"], ALTERNATIVE_ANALYSIS_CATEGORY],
              AA_OUTLINE_COLOR,
              ["==", ["get", "model_category"], BASELINE_CATEGORY],
              BASELINE_FILL_COLOR,
              ["all", ["has", "has_models"], ["get", "has_models"]],
              BASELINE_FILL_COLOR,
              ["has", "ref_key"],
              [
                "match",
                ["get", "ref_key"],
                "rbfs_west",
                BASELINE_FILL_COLOR,
                "rbfs_east",
                BASELINE_FILL_COLOR,
                "ms2",
                BASELINE_FILL_COLOR,
                BASELINE_FILL_COLOR,
              ],
              "#94A3B8",
            ],
            "fill-opacity": [
              "case",
              ["boolean", ["feature-state", "selected"], false],
              0.85,
              ["boolean", ["feature-state", "hover"], false],
              0.75,
              ["==", ["get", "model_category"], ALTERNATIVE_ANALYSIS_CATEGORY],
              0.65,
              ["==", ["get", "model_category"], BASELINE_CATEGORY],
              0.6,
              ["has", "outline_color"],
              0.65,
              ["all", ["has", "has_models"], ["get", "has_models"]],
              0.7,
              ["has", "ref_key"],
              0.7,
              0.2, // Default opacity
            ],
          },
        },
        {
          id: "dmqt-layer-boundaries-lines",
          type: "line",
          source: "dmqt-layer-boundaries",
          paint: {
            "line-color": [
              "case",
              [
                "all",
                ["boolean", ["feature-state", "selected"], false],
                [
                  "==",
                  ["get", "model_category"],
                  ALTERNATIVE_ANALYSIS_CATEGORY,
                ],
              ],
              AA_SELECTED_LINE_COLOR,
              ["boolean", ["feature-state", "selected"], false],
              BASELINE_SELECTED_LINE_COLOR,
              ["has", "outline_color"],
              ["get", "outline_color"],
              ["==", ["get", "model_category"], ALTERNATIVE_ANALYSIS_CATEGORY],
              AA_LINE_COLOR,
              ["==", ["get", "model_category"], BASELINE_CATEGORY],
              BASELINE_LINE_COLOR,
              ["all", ["has", "has_models"], ["get", "has_models"]],
              BASELINE_LINE_COLOR,
              ["has", "ref_key"],
              [
                "match",
                ["get", "ref_key"],
                "rbfs_west",
                BASELINE_LINE_COLOR,
                "rbfs_east",
                BASELINE_LINE_COLOR,
                "ms2",
                BASELINE_LINE_COLOR,
                BASELINE_LINE_COLOR,
              ],
              "#64748B",
            ],
            "line-width": [
              "case",
              ["boolean", ["feature-state", "selected"], false],
              3,
              ["boolean", ["feature-state", "hover"], false],
              2.5,
              ["has", "outline_color"],
              2.5,
              ["all", ["has", "has_models"], ["get", "has_models"]],
              1.5,
              ["has", "ref_key"],
              1.5,
              1, // Default width
            ],
            "line-opacity": [
              "case",
              ["boolean", ["feature-state", "selected"], false],
              1,
              ["boolean", ["feature-state", "hover"], false],
              0.9,
              ["==", ["get", "model_category"], BASELINE_CATEGORY],
              0.4,
              ["all", ["has", "has_models"], ["get", "has_models"]],
              0.9,
              ["has", "ref_key"],
              0.9,
              0.7, // Default opacity
            ],
          },
        },
        {
          id: "dmqt-layer-boundaries-hit",
          type: "line",
          source: "dmqt-layer-boundaries",
          paint: {
            "line-color": "#000000",
            "line-width": 14,
            "line-opacity": 0,
          },
        },
      ];

      // Add or update layers
      layers.forEach((layer) => {
        if (!map.getLayer(layer.id)) {
          map.addLayer(layer);
        }
      });

      // Add event listeners if they haven't been added yet
      if (
        !map._countyEventListenersAdded ||
        map._eventHandlerVersion !== addLayerEventListeners
      ) {
        addLayerEventListeners(map);
        map._countyEventListenersAdded = true;
        map._eventHandlerVersion = addLayerEventListeners;
      }
    },
    [addLayerEventListeners, mapRef],
  );

  const fetchCountiesFeatures = useCallback(async () => {
    const map = mapRef.current?.getMap();
    if (!map) throw new Error("Map not initialized");

    // Get the base counties data from the existing source
    const baseCountiesSource = map.getSource("base-counties");
    if (!baseCountiesSource) throw new Error("Base counties source not found");

    const baseCountiesData = baseCountiesSource._data;

    // Fetch only the models data
    const modelsData = await apiClient.getRequest(
      "/counties/with-models",
      apiType,
      currentHeaders,
    );

    // Merge the data
    return baseCountiesData.features
      .map((feature) => {
        const countyName = feature.properties.cnty_nm;
        const hasModels = modelsData.some(
          (model) =>
            model.toLocaleLowerCase() === countyName.toLocaleLowerCase(),
        );
        if (hasModels) {
          return {
            ...feature,
            properties: {
              ...feature.properties,
              has_models: hasModels,
              ref_key: "county_name",
              ref_value: countyName,
              display_name: countyName + " County",
            },
          };
        } else {
          return null;
        }
      })
      .filter(Boolean);
  }, [apiClient, apiType, mapRef]);

  const layerFeatureKey = (layer) =>
    layer.modelCategory ? `${layer.id}:${layer.modelCategory}` : layer.id;

  const fetchSourceFeatures = useCallback(
    async (layer) => {
      const params = new URLSearchParams();
      if (layer.modelCategory) {
        params.set("model_category", layer.modelCategory);
      }
      const query = params.toString();
      const endpoint = query ? `${layer.endpoint}?${query}` : layer.endpoint;
      const response = await apiClient.getRequest(
        endpoint,
        apiType,
        currentHeaders.current,
      );
      if (!response) throw new Error("No response from API");

      const geojsonData = response;
      if (!Array.isArray(geojsonData?.features)) {
        throw new TypeError(
          "Invalid response format: expected a GeoJSON FeatureCollection",
        );
      }
      return geojsonData.features;
    },
    [apiClient, apiType],
  );

  const paintActiveFeatures = useCallback(
    (layer) => {
      const allFeatures = [];
      activeFeatures.current.forEach((features) => {
        allFeatures.push(...features);
      });
      allFeatures.sort(
        (left, right) => categoryDrawOrder(left) - categoryDrawOrder(right),
      );
      const combinedGeoJSON = {
        type: "FeatureCollection",
        features: assignRasOutlineColors(allFeatures),
      };
      addLayerLayers(layer, combinedGeoJSON);
      return combinedGeoJSON;
    },
    [addLayerLayers],
  );

  // Update layer data (fetch if needed, then update map)
  const updateLayerData = useCallback(
    async (layer, isVisible) => {
      try {
        const key = layerFeatureKey(layer);

        if (isVisible) {
          const features =
            layer.id === "counties"
              ? await fetchCountiesFeatures()
              : await fetchSourceFeatures(layer);
          activeFeatures.current.set(key, features);
        } else {
          activeFeatures.current.delete(key);
        }

        return paintActiveFeatures(layer);
      } catch (error) {
        console.error("Error updating layer data:", error);
      }
    },
    [fetchCountiesFeatures, fetchSourceFeatures, paintActiveFeatures],
  );

  // Load more layer models
  const loadMoreLayerModels = useCallback(() => {
    if (loadingModels || !hasMoreModels) return;
    const nextPage = modelPage + 1;
    setModelPage(nextPage);
    fetchLayerModels(
      searchData.key,
      searchData.value,
      nextPage,
      searchData.modelCategory,
    );
  }, [loadingModels, hasMoreModels, modelPage, searchData, fetchLayerModels]);

  return {
    selectedLayer,
    setSelectedLayer,
    layerModels,
    loadingModels,
    hasMoreModels,
    modelPage,
    totalModels,
    isPanelVisible,
    setIsPanelVisible,
    fetchLayerModels,
    updateLayerData,
    loadMoreLayerModels,
    addLayerLayers,
  };
};

export default useLayerData;
